import { activeRecordingTransportAtom } from '@/atoms/chronicle';
import {
  lowDiskSpaceAcceptedAfterTsAtom,
  lowDiskSpaceAtom,
} from '@/atoms/lowDiskSpaceAtom';
import {
  resolveAppsLowDiskWarningDesktopGate,
  resolveWindowsCommsDefaultMicGate,
  resolveWindowsSystemAudioCaptureGate,
  useIsAppsRustChunkUploadDesktopEnabled,
  useIsAppsUseTranscriptionServiceV2DesktopEnabled,
  useIsAppsWavWriterRecoveryDesktopEnabled,
  useIsAppsZombieRecorderTakeoverDesktopEnabled,
} from '@/features/flags';
import { getDesktopRecorderStartTimeoutMs } from '@/features/flags/desktop-recorder-start-timeout';
import { useIsAppsRustAuth0TokenRefreshDesktopEnabled } from '@/features/flags/rust-auth0-token-refresh-desktop';
import { useIsUserStartFenceKilled } from '@/features/flags/user-start-fence-kill-flag';
import { getWavWriterRecoveryPayload } from '@/features/flags/wav-writer-recovery-payload';
import { useCurrentInputLanguages } from '@/hooks/useCurrentInputLanguages';
import { registerV2Recording } from '@/lib/SessionRecordingProvider/t11nClient';
import { trackFailedToStartChunkRecording } from '@/lib/analytics/scribe/general';
import {
  sessionResumeRecording,
  sessionStartRecording,
} from '@/lib/analytics/scribe/session';
import {
  DesktopCaptureError,
  DesktopCaptureFailure,
  type DesktopCaptureFailureName,
} from '@/lib/telemetry/desktopCaptureErrors';
import {
  appendRecordingAttemptCheckpoint,
  finishRecordingAttempt,
  setRecordingAttemptTransport,
  type RecordingAttemptTracking,
  type RecordingAttemptTrackingKey,
} from '@/lib/telemetry/recordingStartTracking';
import {
  createPostCaptureSetupError,
  createSessionFlowPreflightError,
} from '@/lib/telemetry/sessionFlowFailures';
import { pTimeout } from '@/lib/utils/pTimeout';
import { updateSessionById } from '@/pages/api/scribeAPI';
import { store, type Dispatch } from '@/store';
import { releaseIdleStreamForCapture } from '@/tauri/audio-recorder/idle-stream-handover';
import { deriveCaptureMode, deriveInputDefaultKind } from '@/tauri/audioDeviceUtils';
import {
  PROCESS_DEVICE_PREFIX,
  SYSTEM_DEFAULT_DEVICE_MARKER,
  SYSTEM_WIDE_DEVICE_MARKER,
} from '@/tauri/constants';
import { recorderFmc } from '@/tauri/controller/recording';
import {
  attachOnlineChunkUploadListeners,
  detachOnlineChunkUploadListeners,
} from '@/tauri/online-chunk-upload/listeners';
import { onlineChunkUploadBridge } from '@/tauri/online-chunk-upload/session';
import { classifyRecorderLiveness } from '@/tauri/recorder-liveness';
import { resolveDeviceNamesForStart } from '@/tauri/resolveDeviceNamesForStart';
import {
  resolveEffectiveTranscriptionService,
  TRANSCRIPTION_V2_FALLBACK_TAG,
} from '@/tauri/transcription/effective-service';
import { type CreateLiveTranscriptionResponse } from '@/types';
import { trackSessionDetailResumeStarted } from '@/utils/analytics/track-session-resume-events';
import { getAuthTokenForHttpClient } from '@/utils/http/getAuthTokenForHttpClient';
import {
  isRecordingIdRetriesExhausted,
  recordingIdRequestConfig,
} from '@/utils/http/recordingIdRequest';
import { logger } from '@/utils/logger/logger';
import { getScribeBaseUrlFromV2, scribeHttpV2 } from '@/utils/scribeHTTP';
import {
  beginMidRecordingRestart,
  beginUserRecordingStart,
  forgetMidRecordingRestarts,
  whenFencedStartsSettle,
  type RecordingStartFence,
  type RecordingStartKind,
  type RecordingStartOutcome,
  type StandDownStopFacts,
} from '@heidi/capture';
import { toast } from '@heidi/design-system/toast';
import { useTranslations } from '@heidi/runtime/i18n';
import { sanitizeError } from '@heidi/runtime/logger';
import { getActiveTrackings } from '@heidi/runtime/performance';
import { RecordingStatus } from '@heidi/schema/ml-scribe';
import {
  GET_SESSION_BY_ID,
  type QuerySessionResponse,
  type SessionResponse,
} from '@heidi/session/contracts';
import { activeSessionIdAtom } from '@heidi/session/data';
import { isWindows } from '@heidi/utils';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSetAtom, useStore } from 'jotai';
import posthog from 'posthog-js';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { type AvailableDevices } from 'src-tauri/src/bindings/AvailableDevices';
import { type RecordingLiveness } from 'src-tauri/src/bindings/RecordingLiveness';
import { preserveStaleRecorderAudio } from './preserveStaleRecorderAudio';
import {
  START_TAURI_RECORDING_MUTATION_KEY,
  STOP_RECORDING_MUTATION_KEY,
} from './recordingMutationKeys';
import {
  isPTimeoutRejection,
  isRecorderStartTimeout,
  resolveDesktopChunkIntervalSecs,
} from './useMutateStartTauriRecording.constants';
import { startTimeoutMessages } from './useMutateStartTauriRecording.messages';
import {
  createStartPhaseRecorder,
  isStartPhaseTimeout,
} from './useMutateStartTauriRecording.phases';
import { useMutateGetAndUploadChunkRecording } from './useMutateUploadChunkRecording';

export {
  isPTimeoutRejection,
  isRecorderStartTimeout,
  RECORDER_START_TIMEOUT_MS,
  RECORDING_START_TIMEOUT_ERROR_CODE,
  resolveDesktopChunkIntervalSecs,
} from './useMutateStartTauriRecording.constants';

/** Append only to the click-site entry owned by this invocation. */
const appendDesktopStartRecordingCheckpoint = (
  tracking: RecordingAttemptTracking | undefined,
  checkpointName: string
): void => {
  if (!tracking) return;
  appendRecordingAttemptCheckpoint(tracking, checkpointName);
};

/** Finish only the click-site entry owned by this invocation. */
const finishDesktopStartRecordingTrackingSuccess = (
  tracking: RecordingAttemptTracking | undefined,
  metadata?: Record<string, unknown>
): void => {
  if (!tracking) return;
  finishRecordingAttempt(tracking, { metadata });
};

/** Long enough for the native stop to stop capture; it keeps running past this. */
const LATE_RESTART_STOP_TIMEOUT_MS = 60_000;

// Name, HTTP status and axios code only: desktop error messages can carry PHI.
const errorLogFields = (error: unknown) => ({
  errorName: error instanceof Error ? error.name : typeof error,
  ...sanitizeError(error),
});

const closeDesktopStartRecordingTrackingOnFailure = (
  tracking: RecordingAttemptTracking | undefined,
  error: unknown,
  metadata?: Record<string, unknown>
): void => {
  if (!tracking) return;
  finishRecordingAttempt(tracking, {
    outcome: 'failed',
    error:
      error ??
      createSessionFlowPreflightError(
        'StartRecordingFailed',
        'Desktop recording start ended without a result'
      ),
    metadata,
  });
};

/**
 * Exported so the silent-capture fixes can wait for an in-flight restart to
 * settle before trusting `get_recording_state` (it reports `Recording` before
 * `startSession` can still fail and roll back).
 */
export { START_TAURI_RECORDING_MUTATION_KEY as START_TAURI_RECORDING };

// Rust's begin_stop rejects with this when the recorder is already Stopped,
// which is the desired state for a defensive stop.
const isRecorderAlreadyStopped = (error: unknown) =>
  error instanceof Error && error.message.includes('Recording not started');

// stopSession rejects with this code when no upload session is active, the only
// rejection proving the graceful stop touched nothing (so a bare stop is safe).
const isNoActiveUploadSession = (error: unknown) =>
  (error as { errorCode?: string } | null)?.errorCode === 'ONLINE_SESSION_NOT_CONFIGURED';

/** Device-loss recovery stage marks (`performance.now()` based), threaded to one timing log line. */
export type DeviceLossRecoveryTiming = {
  /** When the device-loss event was consumed by the recovery runner. */
  recoveryStartMs: number;
  /** Time the runner spent enumerating devices. */
  enumerateMs: number;
  /** When the Rust stop (including segment rotation) resolved. */
  stopDoneMs?: number;
};

/**
 * One structured line attributing the recovery gap to its stages.
 *
 * Survivor-biased: it is only called on success, so a recovery that times out
 * or fails at `recorderFmc.start` is never counted and this cannot show a
 * failure rate. Emit from the `catch` too (or add an `outcome` field) to fix.
 * The `stopDoneMs === undefined` branch is a defensive default, since only the
 * rust-chunk-upload path forwards `recoveryTiming` today.
 */
export const logRecoveryTiming = (
  sessionId: string,
  { recoveryStartMs, enumerateMs, stopDoneMs }: DeviceLossRecoveryTiming
) => {
  const now = performance.now();
  logger.info('DESKTOP_DEVICE_LOSS_RECOVERY_TIMING', {
    sessionId,
    recovery_ms: Math.round(now - recoveryStartMs),
    stages: {
      enumerate_ms: Math.round(enumerateMs),
      stop_ms:
        stopDoneMs === undefined
          ? undefined
          : Math.round(stopDoneMs - recoveryStartMs - enumerateMs),
      restart_ms: stopDoneMs === undefined ? undefined : Math.round(now - stopDoneMs),
    },
  });
};

export function useMutateStartTauriRecording() {
  const { t } = useTranslations();
  const intl = useIntl();
  const queryClient = useQueryClient();
  const dispatch = useDispatch<Dispatch>();
  const jotaiStore = useStore();
  const setActiveSessionId = useSetAtom(activeSessionIdAtom);
  const setActiveRecordingTransport = useSetAtom(activeRecordingTransportAtom);
  const { mutate: getChunkRecordingAndUpload } = useMutateGetAndUploadChunkRecording();

  // Roll back the optimistic state useRecordingButton's onMutate set (the
  // active session atom and cached recording_status) when a start aborts or
  // fails, so navigation is not blocked and no phantom "recording" state shows.
  const rollbackOptimisticRecording = (sessionId: string) => {
    // Only clear the atoms if this session still owns them, so a losing or
    // duplicate start cannot wipe the winner's state.
    if (jotaiStore.get(activeSessionIdAtom) === sessionId) {
      setActiveSessionId(null);
      setActiveRecordingTransport(null);
    }
    // Cache rollback is already scoped by sessionId, so it stays unconditional.
    queryClient.setQueryData<SessionResponse>(
      [GET_SESSION_BY_ID, { sessionId }],
      (old) => (old ? { ...old, recording_status: RecordingStatus.IDLE } : undefined)
    );
  };
  // Flags are read at the hook top and captured by mutationFn, then locked into
  // Redux at session start so a mid-session flip cannot split the start, stop
  // and chunk upload paths.
  const useTranscriptionServiceV2 = useIsAppsUseTranscriptionServiceV2DesktopEnabled();
  const rustChunkUploadEnabled = useIsAppsRustChunkUploadDesktopEnabled();
  const wavWriterRecoveryEnabled = useIsAppsWavWriterRecoveryDesktopEnabled();
  // Lock Rust Auth0 force-refresh at session start (`online_chunk_upload_start`).
  const rustAuth0TokenRefreshEnabled = useIsAppsRustAuth0TokenRefreshDesktopEnabled();
  // Kill switch for the zombie takeover below; OFF restores the owned-recorder skip.
  const zombieTakeoverEnabled = useIsAppsZombieRecorderTakeoverDesktopEnabled();
  // Kill switch for the user-start fence; ON leaves user starts unfenced, as before it.
  const userStartFenceKilled = useIsUserStartFenceKilled();
  const languages = useCurrentInputLanguages();
  // Whether this session's recorder is the one capturing; an unreadable answer is not,
  // so a stand-down never stops a recorder another session may own.
  const recorderLiveForSession = async (sessionId: string) => {
    try {
      const liveness = await pTimeout(recorderFmc.get_recording_liveness(), 5000);
      return liveness.state !== 'Stopped' && liveness.session_id === sessionId;
    } catch (error) {
      logger.warn('DESKTOP_RECORDING_LATE_RESTART_LIVENESS_UNREADABLE', {
        sessionId,
        ...errorLogFields(error),
      });
      return false;
    }
  };
  // Stops this session's recorder when a stand-down cannot stop it through the session.
  const killLateStartCapture = async (
    sessionId: string,
    startKind: RecordingStartKind
  ) => {
    if (!(await recorderLiveForSession(sessionId))) return;
    await recorderFmc.stop_without_backup_file().catch((stopError: unknown) => {
      if (isRecorderAlreadyStopped(stopError)) return;
      logger.error('DESKTOP_RECORDING_LATE_RESTART_KILL_FAILED', {
        sessionId,
        startKind,
        ...errorLogFields(stopError),
      });
    });
  };
  // A stop landed while this start was starting: take down only what the start
  // brought up by `at`, so capture never runs after the stop with no owner. An
  // upload session is stopped only once this start owns one, never another session's.
  const standDownLateStart = async (
    sessionId: string,
    fence: RecordingStartFence,
    startKind: RecordingStartKind,
    at: 'before_capture' | 'before_upload' | 'after_upload'
  ): Promise<RecordingStartOutcome> => {
    logger.warn('DESKTOP_RECORDING_LATE_RESTART_STOPPED', {
      sessionId,
      startKind,
      at,
      stopReason: fence.reason,
      quarantineWav: fence.quarantineWav,
    });
    dispatch.timerModel.onPause();
    if (at === 'before_capture') return { kind: 'none' };
    // A user stop has already released the stamp and the session id, so drop the
    // stamp this start set unless another session now owns it. A pause keeps it.
    const clearOwnTransport = () => {
      if (fence.reason !== 'userStop') return;
      if (jotaiStore.get(activeRecordingTransportAtom) !== 'desktop-mic') return;
      const activeSessionId = jotaiStore.get(activeSessionIdAtom);
      if (activeSessionId === null || activeSessionId === sessionId) {
        setActiveRecordingTransport(null);
      }
    };
    if (at === 'before_upload') {
      await killLateStartCapture(sessionId, startKind);
      clearOwnTransport();
      return { kind: 'stoodDown', stop: undefined, stillStopping: false };
    }
    const owner = await pTimeout(onlineChunkUploadBridge.getActive(), 5000).catch(
      (error: unknown) => {
        logger.warn('DESKTOP_RECORDING_LATE_RESTART_OWNER_UNREADABLE', {
          sessionId,
          startKind,
          ...errorLogFields(error),
        });
        return undefined;
      }
    );
    if (owner?.active && owner.sessionId !== sessionId) {
      logger.warn('DESKTOP_RECORDING_LATE_RESTART_STAND_DOWN_SKIPPED', {
        sessionId,
        startKind,
        reason: 'other_session_owns_upload',
      });
      return { kind: 'stoodDown', stop: undefined, stillStopping: false };
    }
    let stop: StandDownStopFacts | undefined;
    let stillStopping = false;
    if (owner?.active) {
      try {
        stop = await pTimeout(
          onlineChunkUploadBridge.stopSession({
            reason: fence.reason,
            quarantineWav: fence.quarantineWav,
            ...fence.stopSettings,
          }),
          LATE_RESTART_STOP_TIMEOUT_MS
        );
      } catch (error) {
        stillStopping = isPTimeoutRejection(error);
        logger.error('DESKTOP_RECORDING_LATE_RESTART_STOP_FAILED', {
          sessionId,
          startKind,
          timedOut: stillStopping,
          ...errorLogFields(error),
        });
        // A timed-out stop is still running natively and owns the recorder.
        if (!stillStopping) await killLateStartCapture(sessionId, startKind);
      }
    } else {
      // No upload session to stop, or it cannot be read: stop only this session's recorder.
      await killLateStartCapture(sessionId, startKind);
    }
    detachOnlineChunkUploadListeners();
    clearOwnTransport();
    return { kind: 'stoodDown', stop, stillStopping };
  };

  return useMutation({
    mutationKey: [START_TAURI_RECORDING_MUTATION_KEY],
    networkMode: 'always',
    retry: 0,
    mutationFn: async (input: {
      sessionId: string;
      /** True for the restart half of a mid-recording device change; keeps warned marks and instance-id high-water marks. */
      isMidRecordingRestart?: boolean;
      /** True when play resumed a paused session. Passed in because the cache is already rewritten to RECORDING. */
      isResumeOfPausedSession?: boolean;
      /**
       * The click-site tracking entry owned by this user-initiated start.
       * Capture restarts do not own a click-site attempt and must omit it.
       */
      tracking?: RecordingAttemptTracking;
      /** @deprecated Pass `tracking`; retained for direct callers during migration. */
      trackingKey?: RecordingAttemptTrackingKey;
      /** Device list the recovery runner already fetched; skips a second (slow on Windows) enumeration. */
      prefetchedDevices?: AvailableDevices;
      /** Stage marks from the device-loss recovery, logged once the recorder is running. */
      recoveryTiming?: DeviceLossRecoveryTiming;
    }) => {
      // Snapshot legacy key-only callers before the first await. Every later
      // callback still owns this exact instance, never a retry on the same key.
      input.tracking ??= input.trackingKey
        ? {
            trackingKey: input.trackingKey,
            trackingInstanceId:
              getActiveTrackings().get(input.trackingKey)?.instanceId ?? null,
          }
        : undefined;
      setRecordingAttemptTransport(input.tracking, 'desktop_mic');
      const { sessionId } = input;
      // A start of this session that a stop already fenced will stand down rather
      // than go live, so this start waits for it instead of being dropped.
      const fencedStartsSettled = userStartFenceKilled
        ? undefined
        : whenFencedStartsSettle(sessionId);
      // Coalesce duplicate starts. Bails before the try so the rollback never
      // runs; `> 1` because this invocation counts as one in-flight mutation.
      if (
        !fencedStartsSettled &&
        queryClient.isMutating({ mutationKey: [START_TAURI_RECORDING_MUTATION_KEY] }) > 1
      ) {
        logger.warn('DESKTOP_RECORDING_START_ALREADY_IN_FLIGHT', { sessionId });
        if (input.tracking) {
          finishRecordingAttempt(input.tracking, {
            outcome: 'failed',
            error: createSessionFlowPreflightError(
              'StartRecordingCancelled',
              'Recording start coalesced into an in-flight native start'
            ),
            metadata: { preflight_gate: 'native_start_in_flight' },
          });
        }
        return;
      }
      // Registered before the first await, so a stop that lands while this
      // start is still starting always finds and fences it.
      const start = input.isMidRecordingRestart
        ? beginMidRecordingRestart(sessionId)
        : beginUserRecordingStart(sessionId);
      const startKind = start.kind;
      if (startKind === 'user') forgetMidRecordingRestarts(sessionId);
      // A user start checks the fence before each step that brings capture up. A
      // restart keeps its single check after `startSession`, which a reconcile waits on.
      const userStartFence = () =>
        startKind === 'user' && !userStartFenceKilled ? start.fence() : undefined;
      // Set once a stop superseded this start; the stop then owns the session state.
      let stoodDown = false;
      // What a stand-down after a throw must take down: the recorder once it started.
      let standDownFrom: 'before_capture' | 'before_upload' = 'before_capture';
      const supersededByStop = () =>
        createSessionFlowPreflightError(
          'StartRecordingCancelled',
          'Recording start superseded by a stop'
        );

      if (fencedStartsSettled) {
        // Registered first, so a stop that lands during the wait fences this start too.
        logger.info('DESKTOP_RECORDING_START_WAITS_FOR_FENCED_START', { sessionId });
        await fencedStartsSettled;
      }

      // A Resume click can race boot recovery after a WebView reload. If Rust
      // already has a live recording for this session, sync ownership and bail
      // (before the try/finally) instead of stopping and restarting the upload.
      // `getActive()` alone is not enough (a dead recorder can leave it true),
      // so also check liveness; anything else falls through to convergence.
      const phases = createStartPhaseRecorder(sessionId);
      try {
        const activeUpload = await phases.run('active_upload_probe', () =>
          onlineChunkUploadBridge.getActive()
        );
        if (activeUpload.active && activeUpload.sessionId === sessionId) {
          const liveness = await phases.run('liveness_probe', () =>
            pTimeout(recorderFmc.get_recording_liveness(), 5000)
          );
          const zombie = classifyRecorderLiveness(liveness);
          const isLiveThisSession =
            liveness.state !== 'Stopped' &&
            !zombie.isZombie &&
            liveness.session_id === sessionId;
          const convergeFence = userStartFence();
          if (isLiveThisSession && convergeFence) {
            // The live recording is not this start's: the stop that fenced it stops it.
            logger.warn('DESKTOP_RECORDING_LATE_RESTART_STOPPED', {
              sessionId,
              startKind,
              at: 'converge',
              stopReason: convergeFence.reason,
              quarantineWav: convergeFence.quarantineWav,
            });
            start.settle({ kind: 'none' });
            dispatch.timerModel.onPause();
            closeDesktopStartRecordingTrackingOnFailure(
              input.tracking,
              supersededByStop(),
              phases.metadata()
            );
            phases.flush();
            return;
          }
          if (isLiveThisSession) {
            logger.warn('DESKTOP_RECORDING_CONVERGE_SKIPPED_ACTIVE_UPLOAD', {
              sessionId,
              recorderState: liveness.state,
            });
            setActiveRecordingTransport('desktop-mic');
            dispatch.tauriRecordModel.setUseRustChunkUpload(true);
            // Restore the whole capture contract, not just transport, so a
            // device change before boot recovery does not use a stale low-disk gate.
            dispatch.tauriRecordModel.setLowDiskMonitorEnabled(
              liveness.low_disk_monitor_enabled
            );
            // Seed the timer here too, since this bail skips the normal start flow.
            const session = queryClient.getQueryData<QuerySessionResponse>([
              GET_SESSION_BY_ID,
              { sessionId },
            ]);
            appendDesktopStartRecordingCheckpoint(input.tracking, 'audio_started');
            dispatch.timerModel.onStart(
              (session?.duration ?? 0) + Math.floor(liveness.elapsed_secs)
            );
            appendDesktopStartRecordingCheckpoint(input.tracking, 'timer_started');
            finishDesktopStartRecordingTrackingSuccess(input.tracking, phases.metadata());
            phases.flush();
            start.settle({ kind: 'none' });
            return;
          }
          logger.warn('DESKTOP_RECORDING_ACTIVE_UPLOAD_WITHOUT_LIVE_RECORDER', {
            sessionId,
            recorderState: liveness.state,
            recorderSessionId: liveness.session_id,
            secsSinceLastData: liveness.secs_since_last_data,
            zombieReason: zombie.reason,
          });
        }
      } catch (probeError) {
        logger.warn('DESKTOP_RECORDING_ACTIVE_UPLOAD_PROBE_FAILED', {
          sessionId,
          ...errorLogFields(probeError),
        });
      }

      // `recordingStarted` flips true once the recorder is running; the finally
      // below is the single rollback site for every other exit.
      let recordingStarted = false;
      // Set when tracking was already finished on a success path.
      let startRecordingTrackingClosed = false;
      // Failure for the tracking attempt the finally closes. Device guards
      // return instead of throwing, so they name their own failure.
      let startFailure: unknown;
      const abortWithCaptureFailure = (
        name: DesktopCaptureFailureName,
        message: string
      ): void => {
        startFailure = new DesktopCaptureError(name, message);
      };

      // Capture the resume signal before `timerModel.onStart` changes `seconds`.
      const timerSecondsAtEntry = store.getState().timerModel.seconds;

      try {
        // Info/warn markers at each outcome keep start failures observable in Datadog.
        logger.info('DESKTOP_RECORDING_START_TRIGGERED', { sessionId });
        const session = queryClient.getQueryData<QuerySessionResponse>([
          GET_SESSION_BY_ID,
          { sessionId },
        ]);
        // Clears `generate_output_without_recording` once a recording begins.
        // Call only after the capture is fully established (recorder and upload
        // pipeline), since a later failure rolls back only `recording_status`.
        // Cache first, then PATCH; a failed PATCH must not abort a live capture.
        // Restarts and resumes retry the PATCH if a refetch restored `true`.
        const clearGenerateOutputWithoutRecording = (): void => {
          if (!session?.generate_output_without_recording) return;
          queryClient.setQueryData<QuerySessionResponse>(
            [GET_SESSION_BY_ID, { sessionId }],
            (old) => (old ? { ...old, generate_output_without_recording: false } : old)
          );
          // MOBILE-PARITY-DEBT(SCR-7755, MFS-209): B6. Each client (web, widget,
          // desktop) clears this flag itself; the backend should clear it when
          // the recording id is minted. Remove this PATCH once it does.
          void updateSessionById(sessionId, {
            generate_output_without_recording: false,
          }).catch(() => {
            // Non-fatal: the cache already shows the cleared flag. Log the
            // classification only, since an error body can carry PHI.
            logger.warn('DESKTOP_RECORDING_CLEAR_OUTPUT_WITHOUT_RECORDING_FAILED', {
              sessionId,
            });
          });
        };
        let { tauriInputDeviceSelected, tauriOutputDeviceSelected } =
          store.getState().scribeRecordModel;

        // The first recording after launch can race boot `getTauriDevices`, so
        // Redux has no selection yet. Enumerate and select now, and skip the
        // later validity refresh so cold start does not enumerate twice.
        let populatedMissingSelection = false;
        if (!tauriInputDeviceSelected || !tauriOutputDeviceSelected) {
          await phases.run('device_enumeration_initial', () =>
            dispatch.scribeRecordModel.getTauriDevices({
              updateSelectedDevices: true,
              prefetchedDevices: input.prefetchedDevices,
            })
          );
          ({ tauriInputDeviceSelected, tauriOutputDeviceSelected } =
            store.getState().scribeRecordModel);
          populatedMissingSelection = true;
        }

        if (!tauriInputDeviceSelected || !tauriOutputDeviceSelected) {
          logger.warn('DESKTOP_RECORDING_START_ABORTED', {
            sessionId,
            reason: 'no_input_or_output_device_selected',
          });
          abortWithCaptureFailure(
            DesktopCaptureFailure.NO_AUDIO_DEVICE,
            'no input or output device selected'
          );
          alert(
            'Please check if you have selected input and output devices, and the required permissions are granted'
          );
          // The finally rolls back the optimistic recording state.
          return;
        }

        // Clear any previously running upload intervals before starting a new session
        const existingInterval = store.getState().tauriRecordModel.interval;
        if (existingInterval) {
          clearInterval(existingInterval);
          dispatch.tauriRecordModel.setInterval(undefined);
        }

        // Fresh silent-capture state; a mid-recording restart keeps the
        // high-water marks and once-per-recording warned marks.
        dispatch.tauriRecordModel.resetCaptureSilence({
          isMidRecordingRestart: input.isMidRecordingRestart,
        });

        // Keep the low-disk fence across a device restart (same capture) and
        // keep another session's warning. The warning's session id is the
        // stronger ownership signal, since the record button already set the atom.
        const activeOwnerAtStart = jotaiStore.get(activeSessionIdAtom);
        const lowDiskWarningAtStart = jotaiStore.get(lowDiskSpaceAtom);
        const warningOwnedByAnotherSession =
          lowDiskWarningAtStart != null && lowDiskWarningAtStart.sessionId !== sessionId;
        const ownsLowDiskState =
          !warningOwnedByAnotherSession &&
          (activeOwnerAtStart == null || activeOwnerAtStart === sessionId);
        // Stamped before the native start so it cannot fence out this capture's own warning.
        const lowDiskFenceTs = Date.now();
        if (!input.isMidRecordingRestart && ownsLowDiskState) {
          jotaiStore.set(lowDiskSpaceAtom, undefined);
          jotaiStore.set(lowDiskSpaceAcceptedAfterTsAtom, lowDiskFenceTs);
        }

        // Check device validity. Skipped when the cold-start path above already enumerated.
        if (!populatedMissingSelection) {
          await phases.run('device_enumeration_validate', () =>
            dispatch.scribeRecordModel.getTauriDevices({
              updateSelectedDevices: false,
              prefetchedDevices: input.prefetchedDevices,
            })
          );
        }

        const newInputDevices = store.getState().scribeRecordModel.tauriInputDevices;
        const newOutputDevices = store.getState().scribeRecordModel.tauriOutputDevices;

        // Readiness-aware gates: awaited here so a start before the flag
        // provider hydrates does not treat the static default as a real OFF.
        // A timed-out read treats every gate as OFF, since recording matters more.
        // The start is then degraded (no system audio, no comms mic, no low-disk
        // monitor), so the attempt metadata lists it as a fallback.
        const [systemAudioGate, commsMicGate, lowDiskMonitorGate] = await phases.runOr<
          (string | undefined)[]
        >(
          'flag_gates',
          () =>
            Promise.all([
              resolveWindowsSystemAudioCaptureGate(),
              resolveWindowsCommsDefaultMicGate(),
              input.isMidRecordingRestart
                ? Promise.resolve(undefined)
                : resolveAppsLowDiskWarningDesktopGate(),
            ]),
          []
        );
        const systemAudioCaptureEnabled = systemAudioGate === 'on';
        const commsDefaultMicEnabled = commsMicGate === 'on';
        const lowDiskMonitorEnabled = input.isMidRecordingRestart
          ? Boolean(store.getState().tauriRecordModel.lowDiskMonitorEnabled)
          : lowDiskMonitorGate === 'on';
        // Deferred when another session's warning suggests this start may lose;
        // the commit after a successful native start (below) covers that case.
        if (!input.isMidRecordingRestart && ownsLowDiskState) {
          dispatch.tauriRecordModel.setLowDiskMonitorEnabled(lowDiskMonitorEnabled);
        }

        // "System Default" needs any default device (a communications default
        // counts when the flag is ON); otherwise the selected device must still exist.
        const isValidInputDevice =
          tauriInputDeviceSelected.name === SYSTEM_DEFAULT_DEVICE_MARKER
            ? newInputDevices?.some(
                (d) =>
                  d.is_default || (commsDefaultMicEnabled && d.is_communications_default)
              )
            : newInputDevices?.find((d) => d.name === tauriInputDeviceSelected.name);

        if (!isValidInputDevice) {
          logger.warn('DESKTOP_RECORDING_START_ABORTED', {
            sessionId,
            reason: 'input_device_disconnected',
          });
          abortWithCaptureFailure(
            DesktopCaptureFailure.INPUT_DEVICE_DISCONNECTED,
            'selected input device is no longer present'
          );
          alert(
            'Please select a valid input device. The current selection may have been disconnected'
          );
          return;
        }

        // Validate the output device and resolve stale process PIDs. A PID can
        // change between polls, so fall back to display_name, then the system
        // default, rather than pass a dead PID to recorderFmc.start().
        // The flag-aware resolution runs first so the guards match the resolved target.
        const selectionResolution = resolveDeviceNamesForStart({
          inputDeviceName: tauriInputDeviceSelected.name,
          outputDeviceName: tauriOutputDeviceSelected.name,
          isSystemAudioCaptureEnabled: systemAudioCaptureEnabled,
        });
        let resolvedOutputDevice = tauriOutputDeviceSelected;
        if (selectionResolution.outputDeviceName === SYSTEM_WIDE_DEVICE_MARKER) {
          // Whole-system capture needs no device-presence guard.
        } else if (
          tauriOutputDeviceSelected.name === SYSTEM_DEFAULT_DEVICE_MARKER ||
          tauriOutputDeviceSelected.name === SYSTEM_WIDE_DEVICE_MARKER
        ) {
          if (!newOutputDevices?.some((d) => d.is_default)) {
            logger.warn('DESKTOP_RECORDING_START_ABORTED', {
              sessionId,
              reason: 'no_system_default_output_device',
            });
            abortWithCaptureFailure(
              DesktopCaptureFailure.OUTPUT_DEVICE_UNAVAILABLE,
              'no system default output device'
            );
            alert(t('desktopErrors.noSystemDefaultOutput'));
            return;
          }
        } else {
          const selectedIsProcess =
            tauriOutputDeviceSelected.name.startsWith(PROCESS_DEVICE_PREFIX);
          const exactMatch = newOutputDevices?.find(
            (d) => d.name === tauriOutputDeviceSelected.name
          );
          if (!exactMatch) {
            // Try label match for stale process PIDs (PID can change between polls)
            const labelMatch = selectedIsProcess
              ? newOutputDevices?.find(
                  (d) =>
                    d.name.startsWith(PROCESS_DEVICE_PREFIX) &&
                    d.display_name != null &&
                    d.display_name ===
                      (tauriOutputDeviceSelected.display_name ??
                        tauriOutputDeviceSelected.name)
                )
              : undefined;
            if (labelMatch) {
              // Update store to the fresh entry (new PID) so subsequent renders are consistent
              dispatch.scribeRecordModel.setTauriOutputDevice(labelMatch);
              resolvedOutputDevice = labelMatch;
            } else {
              // App is no longer playing audio, so fall back to system default
              const defaultDevice = newOutputDevices?.find((d) => d.is_default);
              if (!defaultDevice) {
                logger.warn('DESKTOP_RECORDING_START_ABORTED', {
                  sessionId,
                  reason: 'output_device_disconnected',
                });
                abortWithCaptureFailure(
                  DesktopCaptureFailure.OUTPUT_DEVICE_UNAVAILABLE,
                  'selected output device is no longer present'
                );
                alert(t('desktopErrors.outputDeviceDisconnected'));
                return;
              }
              dispatch.scribeRecordModel.setTauriOutputDevice({
                name: SYSTEM_DEFAULT_DEVICE_MARKER,
                is_default: true,
                display_name: null,
                icon_data_url: null,
                is_communications_default: false,
              });
              // Use the concrete default device, not the sentinel, which would
              // be remapped to system-wide capture when the flag is ON.
              resolvedOutputDevice = defaultDevice;
            }
          }
        }

        if (!session?.session_id) {
          logger.warn('DESKTOP_RECORDING_START_ABORTED', {
            sessionId,
            reason: 'session_id_not_found',
          });
          abortWithCaptureFailure(
            DesktopCaptureFailure.SESSION_NOT_FOUND,
            'session not found in cache'
          );
          alert('Session ID not found');
          return;
        }

        // 1. Check microphone permission
        try {
          const micPermissionGranted = await phases.run('mic_permission', () =>
            recorderFmc.check_microphone_permission()
          );
          if (!micPermissionGranted) {
            alert(
              'Please enable microphone access in System Preferences > Security & Privacy > Privacy > Microphone for Heidi.'
            );
            throw new DesktopCaptureError(
              DesktopCaptureFailure.MIC_PERMISSION_DENIED,
              'Tauri: Microphone permission denied'
            );
          }
        } catch (error) {
          logger.error('Error checking microphone permission:', errorLogFields(error));
          // Our own denial carries a fixed message; a native one can carry PHI.
          trackFailedToStartChunkRecording(
            error instanceof DesktopCaptureError
              ? error.message
              : isStartPhaseTimeout(error)
                ? 'Tauri: Microphone permission check timed out'
                : 'Tauri: Microphone permission check failed'
          );
          throw error;
        }

        // 2. Check screen capture permission
        const sc_permission_checker =
          recorderFmc.screencapture_permission_granted_v2.bind(recorderFmc);

        try {
          const scPermissionGranted = await phases.run('screen_permission', () =>
            sc_permission_checker()
          );
          if (!scPermissionGranted) {
            alert(
              'Please enable screen recording permission in System Preferences > Security & Privacy > Privacy > Screen Recording for Heidi to work properly.'
            );
            throw new DesktopCaptureError(
              DesktopCaptureFailure.SCREEN_CAPTURE_PERMISSION_DENIED,
              'Tauri: Screen capture permission denied'
            );
          }
        } catch (error) {
          logger.error('Error checking screencapture permission:', errorLogFields(error));
          // Our own denial carries a fixed message; a native one can carry PHI.
          trackFailedToStartChunkRecording(
            error instanceof DesktopCaptureError
              ? error.message
              : isStartPhaseTimeout(error)
                ? 'Tauri: Screen capture permission check timed out'
                : 'Tauri: Screen capture permission check failed'
          );
          throw error;
        }

        // Fall back to v1 when the EKS base URL is missing; recording matters
        // more than the cutover. Details live on `resolveEffectiveTranscriptionService`.
        const { effectiveUseV2, transcriptionBaseUrl, didFallBackToV1 } =
          resolveEffectiveTranscriptionService(useTranscriptionServiceV2);
        if (didFallBackToV1) {
          logger.warn({
            msg: 'Tauri: v2 flag on but transcription base URL is null — falling back to v1',
            tag: TRANSCRIPTION_V2_FALLBACK_TAG,
            surface: 'tauri_live',
          });
        }

        // v2 starts the timer after the register PUT succeeds, so it does not
        // tick if register fails. v1 keeps its existing pre-call start.
        if (!effectiveUseV2) {
          dispatch.timerModel.onStart(session?.duration || 0);
          appendDesktopStartRecordingCheckpoint(input.tracking, 'timer_started');
        }

        // v2 uses a client-generated UUID + PUT to EKS; v1 gets the id from ml-scribe.
        let recordingId: string;
        // Timed because a slow or failed register holds up every (re)start.
        const registerStartedMs = performance.now();
        const registerFields = () => ({
          sessionId,
          transport: effectiveUseV2 ? 'v2' : 'v1',
          register_ms: Math.round(performance.now() - registerStartedMs),
        });
        try {
          recordingId = await phases.run('register_recording', async (signal) => {
            if (effectiveUseV2) {
              const v2RecordingId = crypto.randomUUID();
              await registerV2Recording(sessionId, v2RecordingId, languages, signal);
              return v2RecordingId;
            }
            const res = await scribeHttpV2.post<CreateLiveTranscriptionResponse>(
              `/sessions/${sessionId}/restful-segment-transcription`,
              undefined,
              recordingIdRequestConfig(sessionId, { signal })
            );
            logger.debug('DESKTOP_RECORDING_REGISTERED', {
              sessionId,
              recordingId: res.data.recording_id,
            });
            if (!res.data.recording_id) {
              dispatch.timerModel.onPause();
              trackFailedToStartChunkRecording('Tauri: Failed to get recording id');
              throw new Error('Failed to get recording id');
            }
            return res.data.recording_id;
          });
        } catch (error) {
          // v1 already started the timer above; pause it on failure. v2's timer
          // is only started after this block succeeds, so no pause needed there.
          if (!effectiveUseV2) {
            dispatch.timerModel.onPause();
          }
          // Type and length tell a non-HTTP failure apart; the message can carry PHI.
          const failure = {
            errorType: error instanceof Error ? error.name : typeof error,
            messageLength: error instanceof Error ? error.message.length : 0,
            ...sanitizeError(error),
          };
          logger.warn('DESKTOP_RECORDING_REGISTER', {
            ...registerFields(),
            outcome: 'failed',
            ...failure,
          });
          logger.error('Failed to generate recording ID', failure);
          trackFailedToStartChunkRecording(
            isStartPhaseTimeout(error)
              ? 'Tauri: Timed out registering recording id'
              : isRecordingIdRetriesExhausted(error)
                ? 'Tauri: Failed to generate recording id, tried 3 times'
                : effectiveUseV2
                  ? 'Tauri: HTTP error registering v2 recording id'
                  : 'Tauri: HTTP error generating recording id'
          );
          throw error;
        }

        logger.info('DESKTOP_RECORDING_REGISTER', { ...registerFields(), outcome: 'ok' });

        // v2: register succeeded, so start the timer.
        if (effectiveUseV2) {
          dispatch.timerModel.onStart(session?.duration || 0);
          appendDesktopStartRecordingCheckpoint(input.tracking, 'timer_started');
        }

        // Lock the v2 boolean before setChunks so the first chunk reads it. It
        // sits outside `chunks` because `setChunks` is a replace reducer.
        dispatch.tauriRecordModel.setUseTranscriptionServiceV2(effectiveUseV2);

        // alert('start recording' + data.recording_id);
        dispatch.tauriRecordModel.setChunks({
          current_index: 0,
          blobs: [],
          session_id: sessionId,
          recording_id: recordingId,
        });

        if (!tauriInputDeviceSelected || !tauriOutputDeviceSelected) {
          dispatch.timerModel.onPause();
          throw new DesktopCaptureError(
            DesktopCaptureFailure.NO_AUDIO_DEVICE,
            'Please select a valid input device'
          );
        }

        // Take the microphone off the idle visualizer before this recording
        // claims it (shared with the offline start path).
        // Best effort. The release is idempotent and already bounds its native stop
        // at 1.5s, so this deadline is only a backstop for the idle-stream emit.
        await phases.runOr(
          'release_idle_stream',
          () => releaseIdleStreamForCapture(sessionId),
          undefined
        );

        // Resolve device names on the post-validation output device and lock
        // the result for this recording. Start-time only: the persisted
        // selection keeps the sentinel. The device list is re-read because the
        // awaits above can take seconds.
        const resolvedStartDevices = resolveDeviceNamesForStart({
          inputDeviceName: tauriInputDeviceSelected.name,
          outputDeviceName: resolvedOutputDevice.name,
          isSystemAudioCaptureEnabled: systemAudioCaptureEnabled,
          inputDevices: store.getState().scribeRecordModel.tauriInputDevices ?? [],
          isCommsDefaultMicEnabled: commsDefaultMicEnabled,
        });
        dispatch.tauriRecordModel.setLockedStartDevices(resolvedStartDevices);
        // The name the recorder actually started with (the fallback retry can change it).
        let startedInputDeviceName = resolvedStartDevices.inputDeviceName;
        let rustUploadToken: string | undefined;
        let rustUploadBaseUrl: string | undefined;
        try {
          // A stale recorder blocks every start. Probe the liveness snapshot, not
          // just State, to tell a zombie from a live recording.
          let staleRecorderLiveness: RecordingLiveness | null = null;
          try {
            staleRecorderLiveness = await pTimeout(
              recorderFmc.get_recording_liveness(),
              5000
            );
          } catch (probeError) {
            logger.warn('DESKTOP_RECORDING_STATE_PROBE_FAILED', {
              sessionId,
              ...errorLogFields(probeError),
            });
          }
          if (
            staleRecorderLiveness !== null &&
            staleRecorderLiveness.state !== 'Stopped'
          ) {
            const classified = classifyRecorderLiveness(staleRecorderLiveness);
            // Kill switch: when OFF, a zombie verdict never overrides ownership.
            const isZombie = zombieTakeoverEnabled && classified.isZombie;
            // An in-flight stop already resolves the recorder.
            const stopInFlight =
              queryClient.isMutating({ mutationKey: [STOP_RECORDING_MUTATION_KEY] }) > 0;
            const recorderOwned =
              jotaiStore.get(activeRecordingTransportAtom) === 'desktop-mic';
            // Never force-stop a live owned recording; a zombie is still converged.
            const isLiveOwnedRecording = recorderOwned && !isZombie;
            if (stopInFlight || isLiveOwnedRecording) {
              logger.warn('DESKTOP_RECORDING_CONVERGE_SKIPPED', {
                sessionId,
                staleRecorderState: staleRecorderLiveness.state,
                elapsedSecs: staleRecorderLiveness.elapsed_secs,
                secsSinceLastData: staleRecorderLiveness.secs_since_last_data,
                stopInFlight,
                recorderOwned,
                isZombie,
                wouldBeZombie: classified.isZombie,
                zombieTakeoverEnabled,
              });
            } else {
              logger.warn('DESKTOP_RECORDING_STALE_RECORDER_DETECTED', {
                sessionId,
                staleRecorderState: staleRecorderLiveness.state,
                elapsedSecs: staleRecorderLiveness.elapsed_secs,
                secsSinceLastData: staleRecorderLiveness.secs_since_last_data,
                zombieReason: classified.reason,
                recorderOwned,
              });
              // Prefer the graceful Rust-side stop (it survives a webview reload
              // and finalizes the prior recording); bare stop only with no session.
              try {
                const gracefulStopResult = await pTimeout(
                  onlineChunkUploadBridge.stopSession({ backgroundBackup: true }),
                  60000
                );
                // backupUploaded=false means extraction failed and the WAVs are
                // the only copy, so preserve them before start() truncates.
                // Skip when the stop deferred: the WAVs were rotated to paths
                // the detached tail owns, so preserving would fail needlessly.
                if (
                  gracefulStopResult.backupUploaded !== true &&
                  gracefulStopResult.backupDeferred !== true
                ) {
                  await preserveStaleRecorderAudio(sessionId, intl);
                } else if (gracefulStopResult.backupDeferred === true) {
                  logger.info('DESKTOP_RECORDING_STALE_AUDIO_PRESERVE_SKIPPED_DEFERRED', {
                    sessionId,
                  });
                }
              } catch (gracefulStopError) {
                // Fall back only on "no active upload session". Any other
                // failure (a timeout above all) may leave the Rust stop still
                // draining the WAVs, so a bare stop or truncating start is unsafe.
                if (!isNoActiveUploadSession(gracefulStopError)) {
                  throw gracefulStopError;
                }
                logger.warn('DESKTOP_RECORDING_STALE_GRACEFUL_STOP_UNAVAILABLE', {
                  sessionId,
                  ...errorLogFields(gracefulStopError),
                });
                try {
                  await pTimeout(recorderFmc.stop_without_backup_file(), 15000);
                } catch (stopError) {
                  // A concurrent stop winning the race is the desired state.
                  if (!isRecorderAlreadyStopped(stopError)) {
                    throw stopError;
                  }
                }
                // Legacy orphan: the WAVs are the only copy, so preserve them.
                await preserveStaleRecorderAudio(sessionId, intl);
              }
              logger.info('DESKTOP_RECORDING_STALE_RECORDER_RECOVERED', { sessionId });
            }
          }

          // One native time budget covers primary and fallback. Rust owns timeout cleanup.
          const startTimeoutMs = getDesktopRecorderStartTimeoutMs();
          const startDeadlineMs = Date.now() + startTimeoutMs;
          const remainingStartBudgetMs = () => Math.max(0, startDeadlineMs - Date.now());
          const startRecorderWithFallback = async () => {
            try {
              await recorderFmc.start(
                resolvedStartDevices.inputDeviceName,
                resolvedStartDevices.outputDeviceName,
                sessionId,
                {
                  // Create PCM taps when Rust owns streaming chunk upload.
                  useChunkStreaming: rustChunkUploadEnabled,
                  enableWavWriterRecovery: wavWriterRecoveryEnabled,
                  wavWriterRecoveryPayload: getWavWriterRecoveryPayload(),
                  enableLowDiskMonitor: lowDiskMonitorEnabled,
                  startTimeoutMs: remainingStartBudgetMs(),
                  isMidRecordingRestart: input.isMidRecordingRestart,
                }
              );
            } catch (error) {
              // The comms-resolved device can vanish before start. Retry once with
              // the raw sentinel, but never after the native deadline.
              if (
                isRecorderStartTimeout(error) ||
                resolvedStartDevices.inputDeviceName === tauriInputDeviceSelected.name
              ) {
                throw error;
              }
              logger.warn(
                'Comms-default resolved mic failed to start; retrying with the system-default sentinel',
                errorLogFields(error)
              );
              await recorderFmc.start(
                tauriInputDeviceSelected.name,
                resolvedStartDevices.outputDeviceName,
                sessionId,
                {
                  useChunkStreaming: rustChunkUploadEnabled,
                  enableWavWriterRecovery: wavWriterRecoveryEnabled,
                  wavWriterRecoveryPayload: getWavWriterRecoveryPayload(),
                  enableLowDiskMonitor: lowDiskMonitorEnabled,
                  startTimeoutMs: remainingStartBudgetMs(),
                  isMidRecordingRestart: input.isMidRecordingRestart,
                }
              );
              startedInputDeviceName = tauriInputDeviceSelected.name;
              // Re-lock with the mic that is actually capturing.
              dispatch.tauriRecordModel.setLockedStartDevices({
                ...resolvedStartDevices,
                inputDeviceName: tauriInputDeviceSelected.name,
              });
            }
          };

          if (rustChunkUploadEnabled) {
            rustUploadToken =
              (await phases.run('auth_token', () => getAuthTokenForHttpClient())) ??
              undefined;
            rustUploadBaseUrl = getScribeBaseUrlFromV2() ?? undefined;
            if (!rustUploadToken || !rustUploadBaseUrl) {
              logger.error('DESKTOP_RECORDING_RUST_UPLOAD_AUTH_MISSING', { sessionId });
              throw new Error('Auth token or base URL missing for online chunk upload');
            }
          }

          const preCaptureFence = userStartFence();
          if (preCaptureFence) {
            stoodDown = true;
            startFailure = supersededByStop();
            start.settle(
              await standDownLateStart(
                sessionId,
                preCaptureFence,
                startKind,
                'before_capture'
              )
            );
            return;
          }
          try {
            await phases.run('native_start', startRecorderWithFallback);
          } catch (error) {
            if (isRecorderStartTimeout(error)) {
              logger.error('DESKTOP_RECORDING_START_TIMEOUT', {
                timeoutMs: startTimeoutMs,
              });
              // ipc_invoke suppresses its generic toast for this typed error.
              toast({
                tone: 'destructive',
                title: intl.formatMessage(startTimeoutMessages.title),
                description: intl.formatMessage(startTimeoutMessages.description),
              });
            }
            throw error;
          }
          // The recorder is running, so the finally must not roll back.
          recordingStarted = true;
          standDownFrom = 'before_upload';
          appendDesktopStartRecordingCheckpoint(input.tracking, 'audio_started');
          // Stamp the transport so stop/pause route to the desktop recorder even
          // after an input switch. Cleared on stop or rollback.
          setActiveRecordingTransport('desktop-mic');
          // This start did not lose the race, so commit the low-disk writes
          // deferred above.
          if (!input.isMidRecordingRestart && !ownsLowDiskState) {
            dispatch.tauriRecordModel.setLowDiskMonitorEnabled(lowDiskMonitorEnabled);
            // Only drop the warning classified as another session's.
            if (jotaiStore.get(lowDiskSpaceAtom) === lowDiskWarningAtStart) {
              jotaiStore.set(lowDiskSpaceAtom, undefined);
            }
            jotaiStore.set(lowDiskSpaceAcceptedAfterTsAtom, lowDiskFenceTs);
          }
          // The native recorder is running; later upload-wiring failures log separately.
          logger.info('DESKTOP_RECORDING_STARTED', {
            sessionId,
            recordingId,
            effectiveUseV2,
            useRustChunkUpload: rustChunkUploadEnabled,
          });
          if (input.recoveryTiming) {
            logRecoveryTiming(sessionId, input.recoveryTiming);
          }
        } catch (error) {
          dispatch.timerModel.onPause();
          logger.error('Failed to start Tauri recording:', errorLogFields(error));
          throw error;
        }

        // Deferred until the recording is fully established (after the upload
        // session starts or the FE interval is armed), so a start that is torn
        // down is not counted. A mid-recording device change re-enters here and
        // takes the resume branch.
        const trackRecordingStarted = () => {
          // Derive from the resolved output name (what reached Rust).
          const captureMode = deriveCaptureMode(
            { ...resolvedOutputDevice, name: resolvedStartDevices.outputDeviceName },
            isWindows
          );
          // Resolve the sentinel so `input_device` carries a real label.
          let inputDefaultKind = deriveInputDefaultKind(tauriInputDeviceSelected);
          let inputDeviceName = tauriInputDeviceSelected.name;
          if (startedInputDeviceName !== tauriInputDeviceSelected.name) {
            // The sentinel was comms-resolved and the recorder started on it.
            inputDefaultKind = 'comms_default';
            inputDeviceName = startedInputDeviceName;
          } else if (tauriInputDeviceSelected.name === SYSTEM_DEFAULT_DEVICE_MARKER) {
            inputDeviceName =
              newInputDevices?.find((d) => d.is_default)?.name ??
              tauriInputDeviceSelected.name;
          }
          const isResume = timerSecondsAtEntry > 0 || (session?.duration ?? 0) > 0;
          if (isResume) {
            sessionResumeRecording({
              sessionId,
              captureMode,
              inputDefaultKind,
            });
            /* Emit only when the resumed session's recording had ended. An
               in-consult pause/resume or a mid-recording device change is not
               a user resume. */
            if (!input.isMidRecordingRestart && !input.isResumeOfPausedSession) {
              trackSessionDetailResumeStarted({
                session_id: sessionId,
                session_id_local: sessionId,
                /* Cached duration covers an app reload; the entry timer covers an unsaved pause. */
                previous_duration: Math.round(
                  Math.max(session?.duration ?? 0, timerSecondsAtEntry)
                ),
                recording_device: 'app',
              });
            }
          } else {
            sessionStartRecording({
              sessionId,
              transcribeMode: session?.transcribe_mode,
              device: inputDeviceName,
              captureMode,
              inputDefaultKind,
            });
          }
        };
        // Default 45s matches the mobile/offline chunk contract; min 30 allows a PostHog rollback.
        const intervalToUpload = resolveDesktopChunkIntervalSecs(
          posthog.getFeatureFlagPayload('desktop-chunk-size')
        );

        // Lock whether Rust owns chunk upload (set on both branches), then route.
        const useRustChunkUpload = rustChunkUploadEnabled;
        dispatch.tauriRecordModel.setUseRustChunkUpload(useRustChunkUpload);

        if (useRustChunkUpload) {
          // Hand the token + endpoints + ids to Rust; it owns the cadence timer,
          // chunk extraction, multipart upload, backup, and `:finish`. No audio
          // bytes cross the IPC boundary; the FE only listens for `online_chunk:*`.
          try {
            if (!rustUploadToken || !rustUploadBaseUrl) {
              throw new Error('Auth token or base URL missing for online chunk upload');
            }
            const token = rustUploadToken;
            const baseUrl = rustUploadBaseUrl;
            // If a stop landed after the recorder started, stand down before any upload
            // session or listener exists.
            const preUploadFence = userStartFence();
            if (preUploadFence) {
              stoodDown = true;
              startFailure = supersededByStop();
              start.settle(
                await standDownLateStart(
                  sessionId,
                  preUploadFence,
                  startKind,
                  'before_upload'
                )
              );
              return;
            }
            // Attach listeners before startSession (the last step), so the
            // catch never has to tear down an active Rust session.
            await attachOnlineChunkUploadListeners(
              sessionId,
              rustAuth0TokenRefreshEnabled
            );
            await onlineChunkUploadBridge.startSession({
              sessionId,
              recordingId,
              baseUrl,
              token,
              useTranscriptionServiceV2: effectiveUseV2,
              transcriptionBaseUrl: effectiveUseV2 ? transcriptionBaseUrl : null,
              languages: effectiveUseV2 ? languages : [],
              chunkIntervalSecs: intervalToUpload,
              rustAuth0TokenRefresh: rustAuth0TokenRefreshEnabled,
            });
            // Lock the cadence for `useMutateStopRecording`'s truncation
            // heuristic, only after `startSession` resolves. The `sessionId`
            // stamp keeps a still-finishing stop for another session from
            // reading it, so the slot must not be cleared before routing.
            dispatch.tauriRecordModel.setLockedChunkInterval({
              sessionId,
              chunkIntervalSecs: intervalToUpload,
            });
          } catch (error) {
            dispatch.timerModel.onPause();
            logger.error(
              'Failed to start Rust online chunk upload:',
              errorLogFields(error)
            );
            // The Rust session is not active here; detach listeners and stop the recorder.
            detachOnlineChunkUploadListeners();
            // Fire-and-forget, but log failures.
            void recorderFmc.stop_without_backup_file().catch((teardownError) => {
              // Already stopped is the desired state, not a failure.
              if (isRecorderAlreadyStopped(teardownError)) return;
              logger.error('DESKTOP_RECORDING_ROLLBACK_STOP_FAILED', {
                sessionId,
                ...errorLogFields(teardownError),
              });
            });
            // The recorder is torn down, so let the finally roll back.
            recordingStarted = false;
            throw error;
          }
          const fence = startKind === 'user' ? userStartFence() : start.fence();
          if (fence) {
            stoodDown = true;
            startFailure = supersededByStop();
            start.settle(
              await standDownLateStart(sessionId, fence, startKind, 'after_upload')
            );
            return;
          }
          start.settle({ kind: 'live', recordingId });
          logger.debug('started (rust chunk upload)');
          // Recording fully established (recorder + Rust upload session).
          clearGenerateOutputWithoutRecording();
          finishDesktopStartRecordingTrackingSuccess(input.tracking, phases.metadata());
          startRecordingTrackingClosed = true;
          trackRecordingStarted();
          return;
        }

        // The legacy upload path never waits on a session, so this is its last check.
        const legacyFence = userStartFence();
        if (legacyFence) {
          stoodDown = true;
          startFailure = supersededByStop();
          start.settle(
            await standDownLateStart(sessionId, legacyFence, startKind, 'before_upload')
          );
          return;
        }
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        const interval = setInterval(() => {
          logger.debug('getting current recording');
          getChunkRecordingAndUpload({
            sessionId: sessionId || '',
            intent: 'periodic',
          });
          // logger.debug('recording', recording);
        }, 1000 * intervalToUpload);

        dispatch.tauriRecordModel.setInterval(interval);
        logger.debug('started');
        // Recording fully established (recorder + FE chunk-upload interval).
        clearGenerateOutputWithoutRecording();
        finishDesktopStartRecordingTrackingSuccess(input.tracking, phases.metadata());
        startRecordingTrackingClosed = true;
        trackRecordingStarted();
      } catch (error) {
        const failedAfterStop = userStartFence();
        if (failedAfterStop && !stoodDown) {
          // The stop owns the session now, so a failure after it landed is not
          // surfaced again and onError does not stop the session a second time.
          logger.warn('DESKTOP_RECORDING_START_FAILED_AFTER_STOP', {
            sessionId,
            ...errorLogFields(error),
          });
          try {
            start.settle(
              await standDownLateStart(
                sessionId,
                failedAfterStop,
                startKind,
                standDownFrom
              )
            );
            stoodDown = true;
            startFailure = supersededByStop();
            return;
          } catch (standDownError) {
            // The stand-down failed too: keep and rethrow the original failure.
            logger.error('DESKTOP_RECORDING_LATE_RESTART_STAND_DOWN_FAILED', {
              sessionId,
              startKind,
              ...errorLogFields(standDownError),
            });
          }
        }
        // The finalizer classifies the attempt from the value that ended it.
        startFailure = error;
        throw error;
      } finally {
        // A guard that returned after a stop landed leaves the session to that stop too.
        if (!stoodDown && userStartFence()) {
          stoodDown = true;
          startFailure ??= supersededByStop();
          dispatch.timerModel.onPause();
        }
        // Any exit not settled above owes a stop nothing: a failed start tears its
        // recorder down, and an unfenced legacy start owns its recorder.
        start.settle({ kind: 'none' });
        const failedPhase = phases.flush(startFailure);
        // Every non-success exit closes tracking here. A failed start rolls back its
        // optimistic state; a stood-down start leaves it to the stop that superseded it.
        if (!recordingStarted || stoodDown) {
          if (!startRecordingTrackingClosed) {
            closeDesktopStartRecordingTrackingOnFailure(
              input.tracking,
              startFailure,
              phases.metadata(failedPhase)
            );
          }
          if (!stoodDown) rollbackOptimisticRecording(sessionId);
        } else if (!startRecordingTrackingClosed) {
          // Audio went live, then a setup step threw and onError stops it.
          closeDesktopStartRecordingTrackingOnFailure(
            input.tracking,
            createPostCaptureSetupError(),
            phases.metadata()
          );
        }
      }
    },
    onError: (_error, variables) => {
      logger.error('error in start recording', errorLogFields(_error));
      // IPC errors toast in ipc_invoke and permission errors use alert(), so only
      // a step deadline needs one here. Rollback lives in mutationFn's finally,
      // since the alert()+return guards resolve as success and never reach onError.
      if (isStartPhaseTimeout(_error)) {
        toast({
          tone: 'destructive',
          title: intl.formatMessage(startTimeoutMessages.title),
          description: intl.formatMessage(startTimeoutMessages.description),
        });
      }
      dispatch.scribeRecordModel._stopOwnRecording({ sessionID: variables.sessionId });
    },
  });
}
